<template>
  <div>
    <div v-if="contractDistribution && contractDistribution.contracts" class="mt-4">
      <div>
        <div>
          <div class="flex sm:flex-row flex-col justify-between">
            <h1 class="text-3xl pb-4 font-extrabold tracking-tight text-gray-900">
              {{ contractDistribution.translatedCategory }}
            </h1>
          </div>
        </div>

        <div>
          <div class="border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Documents contractuels</h2>
              <div>
                <a
                  v-if="contractDistribution.contractualDocuments?.ipid"
                  :href="contractDistribution.contractualDocuments.ipid"
                  class="text-primary-dark underline cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  IPID
                </a>
                <span v-else class="font-bold">—</span>
              </div>
              <div>
                <a
                  v-if="contractDistribution.contractualDocuments?.cg"
                  :href="contractDistribution.contractualDocuments.cg"
                  class="text-primary-dark underline cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Conditions générales
                </a>
                <span v-else class="font-bold">—</span>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4" v-if="contractDistribution.kitLink">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">PLV</h2>
              <div>
                <dd class="text-md text-gray-900">
                  <a
                    :href="contractDistribution.kitLink"
                    class="text-primary-dark underline cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Voir le kit
                  </a>
                </dd>
              </div>
              <div>
                <SecondaryButton @click="handleOrderKit">Commander un kit</SecondaryButton>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Politique d'engagement</h2>
              <div>
                <dt class="text-sm font-bold text-gray-500">Paiement mensuel</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ contractDistribution.cancellationPolicy.monthly }}</dd>
              </div>
              <div>
                <dt class="text-sm font-bold text-gray-500">Paiement annuel</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ contractDistribution.cancellationPolicy.yearly }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Garanties</h2>
              <div class="col-span-2">
                <div class="flex flex-wrap gap-3">
                  <span
                    v-for="coverage in coveragesList"
                    :key="coverage"
                    :class="getCoverageClass(coverage)"
                    class="px-4 py-1 rounded-full text-sm font-semibold"
                  >
                    {{ coverage }}
                  </span>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Vétusté</h2>
              <div class="col-span-2">
                <div class="text-gray-700">
                  <span
                    v-if="
                      (!contractDistribution.depreciation.startMonth ||
                        contractDistribution.depreciation.startMonth === 0) &&
                      (!contractDistribution.depreciation.yearlyIncrease ||
                        contractDistribution.depreciation.yearlyIncrease === 0) &&
                      (!contractDistribution.depreciation.monthlyIncrease ||
                        contractDistribution.depreciation.monthlyIncrease === 0)
                    "
                  >
                    Pas de vétusté
                  </span>
                  <span v-else-if="contractDistribution.depreciation.depreciationType === 'monthly'">
                    {{ contractDistribution.depreciation.monthlyIncrease }}% par mois à partir du
                    {{ contractDistribution.depreciation.startMonth + 1 }}
                    <span v-html="$t('ordinal.' + (contractDistribution.depreciation.startMonth + 1))"></span>
                    mois
                  </span>
                  <span v-else-if="contractDistribution.depreciation.depreciationType === 'annual'">
                    {{ contractDistribution.depreciation.yearlyIncrease }}% par an à partir du
                    {{ contractDistribution.depreciation.startMonth + 1 }}
                    <span v-html="$t('ordinal.' + (contractDistribution.depreciation.startMonth + 1))"></span>
                    mois
                  </span>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Limite de prise en charge</h2>
              <div class="col-span-2">
                <div
                  v-for="limit in contractDistribution.claimLimits"
                  :key="limit.translatedCoverageNames.join(',')"
                  class="text-gray-700 mb-2"
                >
                  <span v-if="limit.period === 'annual'">
                    {{ $t("claims", limit.limitPerPeriod) }}
                    <span v-for="(name, index) in limit.translatedCoverageNames" :key="name">
                      <span class="font-semibold">
                        {{ name }}{{ index < limit.translatedCoverageNames.length - 1 ? " ET " : "" }}
                      </span>
                    </span>
                    par année de couverture, avec un maximum de prise en charge de
                    {{ formatPrice(limit.maxCoverageAmountPerYear) }}
                  </span>
                  <span v-else-if="limit.period === 'contract_lifetime'">
                    {{ $t("claims", limit.limitPerPeriod) }}
                    <span v-for="(name, index) in limit.translatedCoverageNames" :key="name">
                      <span class="font-semibold">
                        {{ name }}{{ index < limit.translatedCoverageNames.length - 1 ? " ET " : "" }}
                      </span>
                    </span>
                    pour toute la durée du contrat, avec un maximum de prise en charge de
                    {{ formatPrice(limit.maxCoverageAmountPerYear) }}
                  </span>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Franchise</h2>
              <div class="col-span-2">
                <div class="space-y-2">
                  <div v-for="coverage in coveragesList" :key="coverage" class="text-gray-700">
                    <span class="font-semibold">{{ coverage }} :</span>
                    <template v-if="getFirstCoverage(coverage)">
                      <template
                        v-if="
                          parseInt(getFirstCoverage(coverage).deductible) === 0 &&
                          parseInt(getFirstCoverage(coverage).minDeductible) === 0
                        "
                      >
                        Pas de franchise
                      </template>
                      <template v-else-if="getFirstCoverage(coverage).deductibleType === 'percentage'">
                        Franchise de {{ getFirstCoverage(coverage).deductible }}% avec un minimum à
                        {{ formatPrice(getFirstCoverage(coverage).minDeductible) }}
                      </template>
                      <template v-else-if="getFirstCoverage(coverage).deductibleType === 'fix'">
                        Franchise de {{ formatPrice(getFirstCoverage(coverage).deductible) }}
                      </template>
                    </template>
                  </div>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div class="mt-4">
          <div class="mt-2 border-t border-gray-100">
            <dl class="bg-white px-4 sm:px-6 py-5 grid grid-cols-2 gap-x-4 gap-y-6">
              <h2 class="col-span-2 text-xl font-bold">Grille tarifaire</h2>
              <div class="col-span-2">
                <Table :columns="pricingColumns" :data="pricingRows">
                  <template #durationSeparator="{ row }">
                    <td class="full-row bg-purple-100 px-4 py-2 font-semibold" :colspan="pricingColumns.length">
                      {{ row.duration }}
                    </td>
                  </template>
                  <template #multiProducts="{ row }">
                    <FieldSpan
                      :style-to-add="
                        row.multiProductsContract ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      "
                      :value="$t(row.multiProductsContract ? 'Yes' : 'No')"
                    />
                  </template>
                </Table>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <Loading />
    </div>
  </div>
</template>

<script>
import { formatPrice } from "@estaly/ui/src/utils/price_formatter";
import { Table, Loading, SecondaryButton } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";
import FieldSpan from "@/components/utils/FieldSpan.vue";

export default {
  components: { Table, FieldSpan, Loading, SecondaryButton },

  data: () => ({
    contractDistribution: null,
    pricingColumns: [
      { field: "priceBand", label: "Tranche de prix" },
      { field: "multiProducts", label: "Multi produits", slot: "multiProducts" },
      { field: "price", label: "Prix", price: true },
    ],
    merchantsColumns: [
      { field: "id", label: "ID" },
      { field: "salesChannel", label: "Nom" },
    ],
    matchedContracts: null,
  }),

  computed: {
    ...mapGetters("store", ["selectedStoreId"]),

    coveragesList() {
      if (!this.contractDistribution?.contracts) return [];
      const all = this.contractDistribution.contracts.flatMap((c) => c.coverages.map((cov) => cov.translatedName));
      return [...new Set(all)];
    },

    pricingRows() {
      if (!this.contractDistribution?.contracts || !this.matchedContracts) return [];
      const rows = [];
      let lastDuration = null;
      this.contractDistribution.contracts.forEach((contract) => {
        const duration = contract.monthly ? "Mensuel" : `One shot - ${contract.termLength} mois`;
        if (duration !== lastDuration) {
          rows.push({
            fullRow: true,
            slot: "durationSeparator",
            duration,
          });
          lastDuration = duration;
        }
        const matchedContract = this.matchedContracts?.find((mc) => mc.contractId === contract.id);
        rows.push({
          priceBand: `de ${formatPrice(contract.priceBandMin)} à ${formatPrice(contract.priceBandMax)}`,
          termLength: duration,
          multiProductsContract: contract.multiProductsContract,
          price: matchedContract?.price ?? contract.defaultPrice,
        });
      });
      return rows;
    },
  },

  async mounted() {
    await this.fetchContractDistribution();
    await this.fetchMatchedContractsData();
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("contractDistributions", ["fetch", "orderKit"]),
    ...mapActions("matchedContracts", { fetchMatchedContracts: "fetchIndex" }),

    formatPrice,

    async handleOrderKit() {
      if (!window.confirm("Êtes-vous sûr de vouloir commander un kit ?")) return;

      const response = await this.orderKit({
        id: this.$route.params.id,
        storeId: this.selectedStoreId,
      });

      if (response?.success) {
        this.notify({
          category: "simple",
          type: "success",
          title: "Kit commandé avec succès",
        });
      }
    },

    async fetchContractDistribution() {
      const includes = [
        "contractual_documents",
        "cancellation_policy",
        "depreciation",
        "claim_limits",
        "contracts",
        "coverages",
      ];
      this.contractDistribution = await this.fetch({ id: this.$route.params.id, includes });
    },

    async fetchMatchedContractsData() {
      const data = await this.fetchMatchedContracts({ storeId: this.selectedStoreId });
      this.matchedContracts = data?.matchedContracts;
    },

    getCoverageClass(coverage) {
      const colorClasses = [
        "bg-green-100 text-green-800",
        "bg-blue-100 text-blue-800",
        "bg-yellow-100 text-yellow-800",
        "bg-purple-100 text-purple-800",
        "bg-pink-100 text-pink-800",
        "bg-indigo-100 text-indigo-800",
      ];
      const idx = this.coveragesList.indexOf(coverage);
      return colorClasses[idx % colorClasses.length];
    },

    getFirstCoverage(coverageName) {
      for (const contract of this.contractDistribution.contracts) {
        const coverage = contract.coverages.find((cov) => cov.translatedName === coverageName);
        if (coverage) return coverage;
      }
      return null;
    },
  },
};
</script>
